import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, Renderer2, OnInit, OnDestroy, AfterViewInit, HostListener, OnChanges, SimpleChanges, ChangeDetectorRef, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';

import { LockToggleComponent } from '../lock-toggle/lock-toggle.component';
import { FieldErrorTimeoutService } from '../../services/field-error-timeout.service';

@Component({
  selector: 'app-autocomplete',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LockToggleComponent],
  templateUrl: './autocomplete.component.html'
})

export class AutocompleteComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {

  @Input() label = '';
  @Input() placeholder = 'Empieza a escribir para buscar...';
  @Input() timeoutSeconds = 8;
  @Input() defaultValue: any;
  @Input() searchType: 'local' | 'remote' = 'local';
  @Input() items: any[] = [];
  @Input() remoteSearchFn?: (term: string) => Observable<any[]>;
  @Input() findByIdFn?: (id: any) => Observable<any>;
  @Input() labelKey = 'label';
  @Input() valueKey = 'value';

  @Input() showBlocker = false;
  @Input() disabledLabel = '';

  @Input() enabled = true;
  private lockTouched = false;

  @Output() inputChange = new EventEmitter<string>();
  @Output() valueChange = new EventEmitter<any>();
  @Output() enabledChange = new EventEmitter<boolean>();
  @Output() dirtyChange = new EventEmitter<{ formName: string, isDirty: boolean, isValid: boolean }>();

  @ViewChild('input') input!: ElementRef<HTMLInputElement>;
  @ViewChild('subtitleLabel') subtitleLabel!: ElementRef;
  @ViewChild('inbox') inbox!: ElementRef;
  @ViewChild('container') container!: ElementRef;

  form = new FormGroup({
    search: new FormControl('', Validators.required)
  });

  filtered: any[] = [];
  hasSelected = signal(false);
  offTimeOut = signal(false);
  showError = computed(() => this.offTimeOut() && !this.hasSelected());
  showNoResults = false;
  private viewInitialized = false;
  private isApplyingDefault = false;
  private isUpdating = false;
  private originalValue: any = null;

  private timer: any;
  private destroy$ = new Subject<void>();
  private search$ = new Subject<string>();
  private userEditing = false;

  constructor(
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    private fieldErrorTimeout: FieldErrorTimeoutService
  ) {}

  ngOnInit(): void {
    this.applyPreloadLock();
    this.startTimeout();

    if (this.searchType === 'remote') {
      this.search$
        .pipe(
          debounceTime(400),
          distinctUntilChanged(),
          takeUntil(this.destroy$)
        )
        .subscribe(term => this.searchRemote(term));
    }

    const control = this.form.get('search');
    if (this.enabled) {
      control?.enable();
    } else {
      control?.disable();
    }

    this.form.get('search')?.valueChanges.subscribe((val) => {
      if (this.isUpdating) return;
      const formName = this.label;
      const isValid = this.form.valid;
      const isDirty = val !== '' && val !== null && val !== undefined && val !== this.originalValue;

      if (isDirty !== undefined) {
        this.dirtyChange.emit({ formName, isDirty, isValid });
      }
    });
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    if (this.showError()) {
      this.getStyleError();
    } else {
      this.applyInitialStyles();
    }
    this.applyDefault();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.applyPreloadLock();

    if (changes['defaultValue']) {
      const newValue = changes['defaultValue'].currentValue;
      const echoingOriginal = this.userEditing && newValue == this.originalValue;
      if (!echoingOriginal) {
        this.userEditing = false;
        this.originalValue = newValue;
        if (newValue !== null && newValue !== undefined && newValue !== '') {
          this.isApplyingDefault = true;
          setTimeout(() => {
            this.applyDefault();
          }, 100);
        } else {
          this.isUpdating = true;
          this.form.get('search')?.setValue('', { emitEvent: false });
          this.hasSelected.set(false);
          this.filtered = [];
          this.showNoResults = false;
          this.isApplyingDefault = false;
          this.startTimeout();
          setTimeout(() => {
            this.isUpdating = false;
          }, 50);
        }
      }
    }

    if (changes['items'] && changes['items'].currentValue?.length > 0 && !this.isApplyingDefault && !this.userEditing) {
      this.applyDefault();
    }

    if (changes['enabled'] && !this.lockTouched) {
      const control = this.form.get('search');
      if (this.enabled) {
        control?.enable();
      } else {
        control?.disable();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    clearTimeout(this.timer);
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (!this.container.nativeElement.contains(event.target)) {
      this.filtered = [];
    }
  }

  toggleEnabled() {
    if (!this.showBlocker) return;
    this.lockTouched = true;
    this.enabled = !this.enabled;
    const control = this.form.get('search');
    if (this.enabled) {
      control?.enable();
      this.getStyleEnabled();
    } else {
      control?.disable();
      this.getStyleTextDisabled();
    }
    this.enabledChange.emit(this.enabled);
  }

  onInput(): void {
    if (!this.enabled) return;

    const value = this.form.value.search?.trim() || '';
    this.userEditing = true;
    this.hasSelected.set(false);
    this.showNoResults = false;

    this.inputChange.emit(value);

    if (!value) {
      this.filtered = [];
      this.valueChange.emit({ value: null, isValid: false, isDirty: true });
      this.startTimeout();
      return;
    }

    this.getStyleEnabled();

    if (this.searchType === 'local') {
      this.filtered = this.items.filter(item =>
        item[this.labelKey]?.toLowerCase().includes(value.toLowerCase())
      );

      const otraOption = this.items.find(item =>
        item[this.labelKey]?.toUpperCase() === 'OTRA' ||
        item[this.labelKey]?.toUpperCase() === 'OTRO'
      );

      if (otraOption && !this.filtered.some(item => item[this.valueKey] === otraOption[this.valueKey])) {
        this.filtered = [...this.filtered, otraOption];
      }

      if (this.filtered.length === 0 && value.length > 0 && this.label === '¿Cómo se llama la empresa para la que trabajas?') {
        this.filtered = [{ value: 1120, label: 'OTRA' }];
      }

      this.showNoResults = this.filtered.length === 0;
    } else {
      this.search$.next(value);
    }
  }

  private searchRemote(term: string): void {
    if (!this.remoteSearchFn) return;

    this.remoteSearchFn(term).subscribe({
      next: res => {
        this.filtered = Array.isArray(res) ? res : (res ? [res] : []);
        this.showNoResults = this.filtered.length === 0;
        this.cdr.detectChanges();
      },
      error: () => {
        this.filtered = [];
        this.showNoResults = true;
        this.cdr.detectChanges();
      }
    });
  }

  select(item: any, emit = true): void {
    if (!this.enabled) return;

    this.isUpdating = true;
    this.form.get('search')!.setValue(item[this.labelKey]);
    this.filtered = [];
    this.showNoResults = false;

    this.hasSelected.set(true);
    this.offTimeOut.set(false);

    this.getStyleEnabled();

    if (emit) {
      const isDirty = item[this.valueKey] !== this.originalValue;
      this.valueChange.emit({
        value: item[this.valueKey],
        isValid: true,
        isDirty: isDirty
      });
    }

    this.clearTimeout();
    setTimeout(() => {
      this.isUpdating = false;
    }, 50);
  }

  onBlur(): void {
    if (!this.enabled) return;

    setTimeout(() => {
      if (!this.hasSelected() && !this.form.get('search')?.value) {
        this.offTimeOut.set(true);
        this.getStyleError();
        this.cdr.detectChanges();
        this.valueChange.emit({ value: null, isValid: false, isDirty: true });
      }
    }, 300);
  }

  private startTimeout(): void {
    if (!this.enabled) return;
    if (this.offTimeOut() && !this.hasSelected()) {
      this.getStyleError();
      return;
    }

    this.clearTimeout();
    this.offTimeOut.set(false);
    this.timer = setTimeout(() => {
      if (this.enabled && (!this.hasSelected() || this.form.invalid)) {
        this.offTimeOut.set(true);
        this.getStyleError();
        this.cdr.detectChanges();
      }
    }, this.fieldErrorTimeout.delayMs(this.timeoutSeconds));
  }

  private clearTimeout(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private resetStyle(): void {
    if (!this.viewInitialized) return;
    this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
    this.renderer.removeClass(this.inbox.nativeElement, 'border-error');
  }

  private safeApplyStyles(callback: () => void) {
    if (this.viewInitialized) {
      callback();
    }
  }

  private applyInitialStyles() {
    if (this.enabled) {
      this.getStyleEnabled();
    } else {
      this.getStyleTextDisabled();
    }
  }

  getStyleError() {
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled');
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled');

        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.addClass(this.inbox.nativeElement, 'border-error');
      }
    });
  }

  getStyleEnabled() {
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled');
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error');

        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.addClass(this.inbox.nativeElement, 'border-enabled');
      }
    });
  }

  getStyleTextDisabled() {
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled');
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error');

        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.addClass(this.inbox.nativeElement, 'border-disabled');
      }
    });
  }

  private applyPreloadLock(): void {
    if (this.lockTouched || !this.showBlocker) return;
    const hasValue = this.defaultValue !== null && this.defaultValue !== undefined && this.defaultValue !== '';
    if (!hasValue) return;
    this.enabled = false;
    this.form.get('search')?.disable({ emitEvent: false });
    if (this.viewInitialized) {
      this.getStyleTextDisabled();
    }
  }

  private applyDefault(): void {
    if (this.userEditing) {
      this.isApplyingDefault = false;
      return;
    }

    if (this.defaultValue == null || this.defaultValue === '' || this.defaultValue === undefined) {
      this.isUpdating = true;
      this.form.get('search')?.setValue('', { emitEvent: false });
      this.hasSelected.set(false);
      this.isApplyingDefault = false;
      setTimeout(() => {
        this.isUpdating = false;
      }, 50);
      return;
    }

    this.originalValue = this.defaultValue;

    const lookupId = this.defaultValue && typeof this.defaultValue === 'object'
      ? (this.defaultValue.id ?? this.defaultValue.value)
      : this.defaultValue;

    if (this.searchType === 'remote' && this.findByIdFn) {
      this.findByIdFn(lookupId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            const item = res?.data ?? res;
            if (item && item[this.labelKey]) {
              this.isUpdating = true;
              this.form.get('search')?.setValue(item[this.labelKey], { emitEvent: false });
              this.hasSelected.set(true);
              this.offTimeOut.set(false);
              this.clearTimeout();

              this.valueChange.emit({
                value: lookupId,
                isValid: true,
                isDirty: false
              });

              setTimeout(() => {
                this.isUpdating = false;
              }, 50);
            }
          },
          error: () => {
            this.isApplyingDefault = false;
          },
          complete: () => {
            this.isApplyingDefault = false;
          }
        });
    } else if (this.searchType === 'local' && this.items?.length) {
      const found = this.items.find(
        (item: any) => {
          return item[this.valueKey] == this.defaultValue;
        }
      );

      if (found) {
        this.isUpdating = true;
        this.form.get('search')?.setValue(found[this.labelKey], { emitEvent: false });
        this.hasSelected.set(true);
        this.offTimeOut.set(false);
        this.clearTimeout();

        this.valueChange.emit({
          value: this.defaultValue,
          isValid: true,
          isDirty: false
        });

        setTimeout(() => {
          this.isUpdating = false;
        }, 50);
      }
      this.isApplyingDefault = false;
    } else {
      this.isApplyingDefault = false;
    }
  }
}
